'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { SuppliersEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

type ProductSupplierItemProps = {
  supplier: SuppliersEntity;
};

export const ProductSupplierItem = ({ supplier }: ProductSupplierItemProps) => {
  const { t } = useTranslations();

  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);

  const product = useVariantsStore(s => s.product);
  const price = supplier.pricing?.customer?.with_vat;

  const isAffiliate = supplier.is_affiliate;

  return (
    <>
      <div
        className={cn(
          'flex w-full flex-col items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 sm:flex-row md:h-20',
          'ea-product-supplier-item',
        )}
      >
        <div className="flex h-[60px] items-center md:ml-8 md:max-h-[60px] md:max-w-[150px]">
          <Image
            src={supplier?.supplier?.image?.url || '/images/placeholder.png'}
            alt={resolveImageAlt(supplier?.supplier?.image, supplier?.supplier?.name, "supplier_img")}
            width="0"
            height="0"
            sizes={buildImageSizes([{ min: 'md', size: '150px' }, { size: '56px' }])}
            className="h-[56px] w-auto md:h-[60px] md:max-w-[150px]"
          />
        </div>
        <div className="flex w-full max-w-[700px] flex-grow flex-col items-end justify-end gap-3 whitespace-nowrap md:flex-row md:items-center md:gap-6">
          <div className="mb-4 flex w-full flex-col items-start gap-2 sm:mb-0 sm:items-end">
            {!!supplier?.supplier?.name && (
              <p className="flex items-center text-wrap text-lg font-semibold">
                {supplier?.supplier?.name}
              </p>
            )}
            {!!supplier.name && (
              <p className="text-md flex items-center text-wrap font-semibold">{supplier.name}</p>
            )}
          </div>
          <div
            className={cn(
              'mb-4 flex w-full items-center text-lg font-bold sm:mb-0 sm:w-auto',
              'ea-product-supplier-item__product-price',
            )}
          >
            {price == null ? '—' : formatPrice(price, currency, currencySettings)}
          </div>
          {isAffiliate ? (
            <div
              className={cn(
                'flex w-full items-center sm:w-auto',
                'ea-product-supplier-item__to-store-button-container',
              )}
            >
              <Button asChild className="flex-grow">
                <a href={supplier.url ?? '#'}>{t('text721')}</a>
              </Button>
            </div>
          ) : (
            <div
              className={cn(
                'flex items-center',
                'ea-product-supplier-item__to-cart-button-container',
              )}
            >
              <AddToCartButton product={product} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
